import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import feedbackService from '../../services/feedbackService';
import './FeedbackModal.scss';

const FeedbackModal = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    type: 'feedback',
    subject: '',
    message: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await feedbackService.submitFeedback({
        type: formData.type,
        subject: formData.subject,
        message: formData.message,
        email: formData.email || (isAuthenticated ? user?.email : null)
      });
      
      setSubmitStatus('success');
      addToast({
        message: 'Thank you! Your feedback has been submitted successfully.',
        type: 'success',
        autoClose: true
      });
      
      // Reset form and close modal after a short delay
      setTimeout(() => {
        onClose();
        setFormData({ type: 'feedback', subject: '', message: '', email: '' });
        setSubmitStatus(null);
      }, 1500);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
      addToast({
        message: error.message || 'Failed to submit feedback. Please try again.',
        type: 'error',
        autoClose: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add animation class management and prevent background scroll
  useEffect(() => {
    if (isOpen) {
      // Get scrollbar width before hiding overflow
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Apply styles to prevent scrolling while preserving scrollbar space
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Also apply to any fixed positioned elements that might shift
      const header = document.querySelector('.menu-bar');
      if (header) {
        header.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      // Reset styles
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      const header = document.querySelector('.menu-bar');
      if (header) {
        header.style.paddingRight = '';
      }
    }
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      const header = document.querySelector('.menu-bar');
      if (header) {
        header.style.paddingRight = '';
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="feedback-modal__overlay" onClick={handleOverlayClick}>
      <div className="feedback-modal">
        <div className="feedback-modal__header">
          <h2 className="feedback-modal__title">Submit Feedback</h2>
          <button 
            className="feedback-modal__close"
            onClick={onClose}
            aria-label="Close feedback modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-modal__form">
          <div className="feedback-modal__field">
            <label htmlFor="feedback-type" className="feedback-modal__label">
              Type
            </label>
            <select
              id="feedback-type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="feedback-modal__select"
              required
            >
              <option value="feedback">General Feedback</option>
              <option value="feature">Feature Request</option>
              <option value="question">Question</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="feedback-modal__field">
            <label htmlFor="feedback-subject" className="feedback-modal__label">
              Subject
            </label>
            <input
              type="text"
              id="feedback-subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              className="feedback-modal__input"
              placeholder="Brief description of your feedback"
              required
            />
          </div>

          {!isAuthenticated && (
            <div className="feedback-modal__field">
              <label htmlFor="feedback-email" className="feedback-modal__label">
                Email (Optional)
              </label>
              <input
                type="email"
                id="feedback-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="feedback-modal__input"
                placeholder="your.email@example.com"
              />
            </div>
          )}

          <div className="feedback-modal__field">
            <label htmlFor="feedback-message" className="feedback-modal__label">
              Message
            </label>
            <textarea
              id="feedback-message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              className="feedback-modal__textarea"
              placeholder="Please provide details about your feedback or feature request..."
              rows={6}
              maxLength={1000}
              required
            />
            <div className={`feedback-modal__char-count ${formData.message.length > 1000 ? 'feedback-modal__char-count--over-limit' : formData.message.length > 900 ? 'feedback-modal__char-count--warning' : ''}`}>
              {formData.message.length}/1000
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="feedback-modal__status feedback-modal__status--success">
              Thank you! Your feedback has been submitted successfully.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="feedback-modal__status feedback-modal__status--error">
              Sorry, there was an error submitting your feedback. Please try again.
            </div>
          )}

          <div className="feedback-modal__actions">
            <button
              type="button"
              onClick={onClose}
              className="feedback-modal__button feedback-modal__button--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="feedback-modal__button feedback-modal__button--primary"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;